import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import { Flex, SecondaryButton, Text } from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import { ColumnGuideTable } from '~/components/common/CsvUpload/ColumnGuideTable'
import { CsvUploadPanel } from '~/components/common/CsvUpload/CsvUploadPanel'
import { iconSize } from '~/constants/iconSize'
import type { ColumnGuideEntry } from '~/models/columnGuide'

/**
 * `export` するのは `UploadWizard` (差し込み口、Design Note
 * `upload-wizard-step1-panel.md` D3) が自分の Props をこの型の合成
 * (`Omit<CsvUploadStep1PanelProps, 'children'> & {...}`) で作るため。
 * 転送するだけの 4 props を呼び出し側で再宣言すると、この型と 2 重に
 * メンテナンスすることになる (Data Clumps)。
 */
export type CsvUploadStep1PanelProps = {
  /**
   * 雛形のダウンロードボタンの並び。 雛形が 1 つの画面は要素 1 つで渡す。 `label` を
   * 省くと全画面共通の「テンプレートをダウンロード」(`common:upload.templatePrep.downloadLabel`)
   * になる。 複数の雛形がある画面 (ブロック入札 CSV は通常ブロック / リンクブロック / ループの
   * 3 つ) はボタンごとに `label` を渡す。
   */
  downloads: Array<{ label?: string; onClick: () => void }>
  /**
   * AC-DUC-035 の記入例削除案内に差し込む代表列とその先頭記入例値。案内文の本体は
   * 全 data type で同一なので component が `common:` で解決し、呼び出し側はこの 2 値
   * だけを渡す (文を組み立てて渡すと共通文が画面ごとに逐語重複する)。
   * `common.md:441` の「※ 記入例を持たない（ヘッダ行のみの）CSV では本案内文を
   * 表示しない」に従い、渡さなければ段落を描画しない。
   */
  sampleRow?: { column: string; example: string }
  /**
   * AC-DUC-037 の記入ガイド表の並び。 表が 1 つの画面は要素 1 つ (`title` なし) で渡す。
   * 複数の表を並べる画面 (ブロック入札 CSV は通常 / リンク / ループの 3 表、 Des 回答 #8786)
   * は表ごとに `title` を渡す。 `common.md:142` の「※ individual spec に記入ガイド表の
   * 定義がない CSV では本表を表示しない」に従い、渡さなければ表を描画しない。
   */
  guideSections?: Array<{
    title?: string
    rows: ColumnGuideEntry[]
    /** 列数メタに出す実列数 (省略時は rows の件数)。 */
    columnCount?: number
  }>
  /**
   * design 見出し「2. 準備したファイルを選択」直下の選択案内文。data type ごとに
   * 文言が異なるため props で受ける。
   *
   * **省略可**。`DropZone` 自身が操作方法 (`common:upload.dropZone.csvTitle` /
   * `.csvCondition` / `.csvSelectButton`) を出すので、同じことを繰り返すだけの案内文は置かない
   * (2026-09-17 Des 判断。スポット CSV は撤去済み)。渡す価値があるのは
   * 「この画面でしか言えないこと」だけ。
   */
  selectHint?: string
  /** 選択段の中身 (dropzone・構文エラー表示・選択済みファイル情報等)。画面ごとに UI が異なるため children で受ける。 */
  children: ReactNode
}

/**
 * CSV アップロード画面 Step1 パネル全体
 * (design panel-step1。見出し `1. CSV ファイルを準備` から見出し
 * `2. 準備したファイルを選択` までを 1 component が所有する)。
 *
 * design (`bilateral-trade.html:70` 見出し1 / `:75` hint / `:79-85` 準備ブロック、
 * `cis-contract-master.html:61` 見出し1 / `:62` hint / `:63-69` 準備ブロック) は
 * 見出し1・準備段 hint・DL ボタン (文言 + アイコン)・記入例案内・見出し2 のいずれも
 * 全画面で同一のため、これらは component 内部で共通 i18n
 * (`common:upload.templatePrep.*` / `common:upload.templateSelect.title`) を
 * 解決する (`~/components/common/CsvUpload/ColumnGuideTable` と同じ判断)。
 * 節番号構成 (見出しの所有) をこの 1 箇所へ集約することで、呼び出し側が
 * 見出し文言・並び順のコメントを逐語コピーしなくなる (issue #7218)。
 *
 * 見出し2 直下の選択案内文 (`selectHint`) と選択段の中身
 * (dropzone・エラー表示等、`children`) は画面ごとに異なるため props で受ける。
 *
 * 縦 spacing は Flex の gap で一括して持つ (要素ごとの margin 簿記と、optional
 * block の在/不在に応じた余白の条件分岐を持たない)。
 *
 * 元は相対取引契約 (issue #6990) と供給地点マスタ (issue #6467) が逐語コピーで
 * 個別実装しており、供給地点マスタ側は準備段の hint を持たない非対称が生じて
 * いた (issue #7218)。この component へ引き上げることで両画面が同じ hint を
 * 表示するようになる。
 *
 * `framed` を渡すと design の `#panel-step1` どおりに描く: 白地パネル
 * (`CsvUploadPanel`) に入れ、 見出しを 20px / bold、 説明を 14px、 雛形 DL を
 * M サイズにし、 見出し 2 の上を 20px 空ける (`.up-panel-title ~ .up-panel-title`)。
 * Step2 も同じパネルで描く画面 (48点データ / 相対取引契約 / 供給地点マスタ) だけが渡す。
 * `UploadWizard` 経由の画面は Step2 がまだパネルでないため、 Step1 だけ変えず従来の形のまま。
 */
export const CsvUploadStep1Panel = ({
  downloads,
  sampleRow,
  guideSections,
  selectHint,
  framed = false,
  children,
}: CsvUploadStep1PanelProps & {
  /** design の `.up-panel` として描くか (上の docstring)。 */
  framed?: boolean
}): React.ReactElement => {
  const { t } = useTranslation()
  const titleTextStyle = framed ? 'uploadPanelTitle' : 'sectionTitle'
  const hintFontSize = framed ? 'md' : 'sm'
  // 見出しと説明の間 (design `.up-panel-hint` の margin-top 8px)
  const headingGap = framed ? 2 : 1

  const content = (
    <Flex flexDirection="column" gap={3}>
      {/* design `<h2 class="up-panel-title">` 相当。パンくず末尾 (bold) が画面名を
          兼ねるため h1 は既に無く、h2 が最上位の見出し階層になる
          (`PageIntro.tsx` の `as="h2"` と同じ判断)。 */}
      <Flex flexDirection="column" gap={headingGap}>
        <Text as="h2" textStyle={titleTextStyle}>
          {t('common:upload.templatePrep.title')}
        </Text>
        <Text color="Text.midEmphasis" fontSize={hintFontSize}>
          {t('common:upload.templatePrep.hint')}
        </Text>
      </Flex>

      {/* アイコンは dictionary「CSV 導線の文言・アイコン規約」の出力方向
          (下向き矢印 + トレイ)。design の `#btn-dl-template` が持つ svg と同じ
          glyph で、前例は `~/components/common/MasterTableActionBar`。 */}
      {downloads.map((download) => {
        const label =
          download.label ?? t('common:upload.templatePrep.downloadLabel')
        return (
          <SecondaryButton
            key={label}
            alignSelf="flex-start"
            onClick={download.onClick}
            size={framed ? 'md' : 'sm'}
          >
            <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
            {label}
          </SecondaryButton>
        )
      })}

      {sampleRow !== undefined && (
        <Text color="Text.midEmphasis" fontSize="sm">
          {t('common:upload.templatePrep.sampleRowHint', {
            column: sampleRow.column,
            example: sampleRow.example,
          })}
        </Text>
      )}

      {/* AC-DUC-037: 雛形ダウンロードと同一パネル (Step1) に記入ガイド表を
          表示する。独立ステップ・別画面にしない。 */}
      {guideSections?.map((section, index) => (
        <ColumnGuideTable
          key={section.title ?? index}
          columnCount={section.columnCount}
          rows={section.rows}
          title={section.title}
        />
      ))}

      <Flex
        flexDirection="column"
        gap={headingGap}
        // 見出し 2 の上は前の塊から 20px (gap 12px + 8px)。 design は 20px/bold の見出しが
        // 前の箱に詰まって見えないよう 2 つ目以降の見出しだけ上を空ける
        marginTop={framed ? 2 : undefined}
      >
        <Text as="h2" textStyle={titleTextStyle}>
          {t('common:upload.templateSelect.title')}
        </Text>
        {/* `whiteSpace="pre-line"` で `\n` を視覚的な改行として表示する
            (design が `<br>` で 2 文に分ける画面向け、 `Dropzone` の
            `specText` と同じ規約)。 単文の呼び出し元 (`\n` を含まない
            selectHint) には視覚差分が無い。
            渡されない画面では段落ごと出さない (DropZone との重複を避ける)。 */}
        {selectHint !== undefined && (
          <Text
            color="Text.midEmphasis"
            fontSize={hintFontSize}
            whiteSpace="pre-line"
          >
            {selectHint}
          </Text>
        )}
      </Flex>

      {children}
    </Flex>
  )

  return framed ? <CsvUploadPanel>{content}</CsvUploadPanel> : content
}
